import type { AiChat } from "@src/store/slices/global";

/**
 * What closing the chat panel means for the session behind it.
 *
 * Modes differ: analytics-style chats are throwaway, experience creation keeps
 * its transcript so an accidental close can be resumed, and Atlas ends the rule
 * build outright — the create-rule page locks its composer and template grid to
 * the entry the session started from, so a lingering `mode`/`initialContext`
 * would strand that page on a rule the user can no longer see.
 *
 * Atlas deliberately does NOT join the experience-creation exception. The
 * reason experience creation keeps its transcript in memory is that losing it
 * loses the conversation; for a rule that is no longer true. The rule row
 * records the session that authored it and the rail loads that transcript back
 * on the next visit (see `useAtlasChatRail`), so the conversation survives the
 * close on its own. Clearing here is load-bearing where keeping is not: it is
 * what unlocks the create page.
 */
export interface AiChatClosePlan {
  /** Stop an in-flight Atlas rule build before tearing the session down. */
  cancelAgenticLogicRun: boolean;
  /** Whether the transcript is dropped rather than kept for a later resume. */
  clearTranscript: boolean;
  /** Chat props to write. `mode: undefined` is what unlocks an owning page. */
  nextProps: (
    | { key: "open"; value: AiChat["open"] }
    | { key: "start"; value: AiChat["start"] }
    | { key: "initialContext"; value: AiChat["initialContext"] }
    | { key: "mode"; value: AiChat["mode"] }
    | { key: "buildingRuleId"; value: AiChat["buildingRuleId"] }
    | { key: "activeScenario"; value: AiChat["activeScenario"] }
  )[];
}

export const planAiChatClose = (mode: AiChat["mode"]): AiChatClosePlan => {
  if (mode === "experience-creation-single-agent") {
    return {
      cancelAgenticLogicRun: false,
      clearTranscript: false,
      nextProps: [{ key: "open", value: false }],
    };
  }

  return {
    cancelAgenticLogicRun: mode === "agentic-logic-creation",
    clearTranscript: true,
    nextProps: [
      { key: "open", value: false },
      { key: "initialContext", value: null },
      { key: "start", value: false },
      { key: "buildingRuleId", value: null },
      { key: "activeScenario", value: null },
      ...(mode === "agentic-logic-creation"
        ? ([{ key: "mode", value: undefined }] as const)
        : []),
    ],
  };
};
